import { useCallback, useRef, useState } from "react";
import { api, errorMessage } from "@/lib/api";

const SESSION_KEY = "757-concierge-session";
export const getConciergeSessionId = () => {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `s${crypto.randomUUID().replaceAll("-", "")}`;
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return `s${Date.now().toString(36)}tmp`;
  }
};

export const resetConciergeSessionId = () => {
  try { localStorage.removeItem(SESSION_KEY); } catch {}
  return getConciergeSessionId();
};

/** Conversation state for the corner concierge (history is stored per visitor + session on the server). */
export function useConciergeChat(onNeedAccount) {
  const sid = useRef(getConciergeSessionId());
  const loaded = useRef(false);
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (loaded.current) return;
    loaded.current = true;
    try {
      const r = await api.get("/ai/agent/history", { params: { session_id: sid.current } });
      const rows = Array.isArray(r.data?.messages) ? r.data.messages : [];
      setMessages((m) => (m.length ? m : rows));
    } catch {
      /* start fresh */
    }
  }, []);

  const addLocal = useCallback((msg) => setMessages((m) => [...m, msg]), []);

  const send = useCallback(async (text, ctx) => {
    const q = text.trim();
    if (!q) return null;
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "", pending: true }]);
    try {
      const r = await api.post("/ai/agent", { message: q, session_id: sid.current, ...ctx });
      const d = r.data ?? {};
      setMessages((m) => [...m.slice(0, -1), { role: "assistant", text: d.answer ?? "", cards: Array.isArray(d.cards) ? d.cards : [] }]);
      return d;
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det?.code === "register_required") {
        setMessages((m) => m.slice(0, -2));
        onNeedAccount(det.msg, () => send(q, ctx));
        return null;
      }
      setMessages((m) => [...m.slice(0, -1), { role: "assistant", text: errorMessage(e), error: true, cards: [{ type: "call" }] }]);
      return null;
    } finally {
      setBusy(false);
    }
  }, [onNeedAccount]);

  return { messages, busy, load, send, addLocal, sessionId: sid.current };
}
